// ✅ App Router only — use this for pages under src/app/
// TODO: Once fully migrated to App Directory, delete usePagesDirectoryStableQueryParams
// and most likely the other helpers in useUpdateQueryParam.tsx
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useMemo } from 'react'

import { useStableCallback } from '@syconium/little-miss-figgy'

import {
  UpdateQueryParamsOptions,
  UseQueryParams,
} from '../../brunswick/containers/CollectionFilterQueryParamsContext'

export type { UpdateQueryParamsOptions }

export function useAppDirectoryStableQueryParams(
  options?: UpdateQueryParamsOptions
): UseQueryParams {
  const params = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()

  const { shallow = true, scroll = false } = options ?? {}

  /** Update (or set) query parameters in the current URL. */
  const updateQueryParams = useStableCallback(
    (updatedQueryParams: Record<string, string | undefined>) => {
      // Read the live URL: `useSearchParams` lags a shallow `replaceState` until the next render,
      // so back-to-back updates would drop each other's params.
      const updatedQuery = new URLSearchParams(window.location.search)
      Object.entries(updatedQueryParams).forEach(([key, value]) => {
        if (typeof value === 'undefined') {
          updatedQuery.delete(key)
        } else {
          updatedQuery.set(key, value)
        }
      })

      const search = updatedQuery.toString()

      if (!shallow) {
        // The router needs an explicit pathname, otherwise clearing every param leaves it
        // with nothing to resolve the navigation against.
        const currentPathname = pathname ?? window.location.pathname
        router.replace(search ? `${currentPathname}?${search}` : currentPathname, { scroll })
        return
      }

      window.history.replaceState(null, '', `?${search}`)
    }
  )

  /** A dictionary that represents the current query parameters that exist in the URL */
  const query = useMemo(() => {
    const queryRecord: UseQueryParams['query'] = {}
    params?.forEach((value, key) => {
      const currentValue = queryRecord[key]
      if (typeof currentValue === 'undefined') {
        queryRecord[key] = value
        return
      }

      queryRecord[key] = Array.isArray(currentValue)
        ? [...currentValue, value]
        : [currentValue, value]
    })
    return queryRecord
  }, [params])

  return { query, updateQueryParams }
}
